import { put, call, all, takeEvery } from 'redux-saga/effects'
import { push } from 'react-router-redux'
import { store as reduxStore } from 'index'
import _ from 'lodash'
import {
  getBillingCycleUsage as getBillingCycleUsageMetrics,
  requestBillingUsage as requestMultiTenantBillingUsage,
  getInsightsData,
  BillingCycleUsage,
  MetricsApp,
  MetricsServiceError,
  MultiTenantUsageResponse,
} from 'services/metrics'
import { checkBillingType, getTrialLeftInDays, getTimestampAsFullHumanDateOrdinal } from 'helpers'
// Relative path — the bare 'constants' specifier collides with Node's builtin.
import constants from '../../constants'
import actions from './actions'
import { AppUsageAndOverages, BillingState } from './reducers'

/**
 * Billing slice sagas. `redux/apps/sagas.js` is the behavioural source of truth;
 * intended differences are listed in docs/overview-migration/PARITY-AUDIT.md.
 */

// The root store is a plain-JS module (untyped). Read its slices through this
// boundary so the rest of the file stays typed.
interface RootState {
  apps: { active: MetricsApp; appSettings: unknown }
  billing: BillingState
}
const getRoot = (): RootState => (reduxStore.getState() as unknown) as RootState

// Every saga is dispatched with `{ type, payload }`; only the payload varies.
type SagaWorker = (action: any) => any

interface AppParameters {
  [key: string]: number | boolean | undefined
}

interface UsageApp extends MetricsApp {
  plan?: string
  createdAt?: number
  trialEndsAt?: number
  parameters: AppParameters
}

export interface UsageAndOveragesPayload {
  data?: UsageApp
  appSettings?: unknown
  /** Overrides the billing window (used by the billing page's date picker). */
  fromDate?: number
  toDate?: number
  /** When set, suppresses the overage calculations. */
  overagesEmpty?: unknown
}

/** Zero-usage shape: no billing term, no usage, or a timed-out request. */
function zeroUsage(parameters: AppParameters): AppUsageAndOverages {
  return {
    mau: 0,
    ccu: 0,
    voice_minutes: 0,
    video_minutes: 0,
    mauLimit: parameters['overages.mau.included'] as number,
    ccuLimit: parameters['overages.ccu.included'] as number,
    mcuLimit: parameters['overages.mcu.included'] as number,
    dcuLimit: parameters['overages.dcu.included'] as number,
    voice_minutes_limit: parameters['overages.calling.included'] as number,
    video_minutes_limit: parameters['overages.calling.included'] as number,
    currentUsageUsers: 0,
    currentUsageUsersUsed: 0,
    ccuUsed: 0,
    voiceMinutesUsed: 0,
    videoMinutesUsed: 0,
    recorded_minutes: 0,
    mau_overage: 0,
    ccu_overage: 0,
    fromDate: '',
    toDate: '',
  }
}

export function* GET_USAGE_AND_OVERAGES({
  payload = {},
  redirectToIntegrate = false,
  callSourceRedirectURL,
}: {
  payload?: UsageAndOveragesPayload
  redirectToIntegrate?: boolean
  callSourceRedirectURL?: string
}): Generator<unknown, AppUsageAndOverages, any> {
  yield put({
    type: actions.SET_STATE,
    payload: { appUsageAndOveragesLoading: true },
  })

  if (!payload.data) payload.data = getRoot().apps.active as UsageApp
  if (!payload.appSettings) payload.appSettings = getRoot().apps.appSettings

  const app = payload.data
  // Defaulted so an app without `parameters` can't throw before any state is
  // written. Missing plan limits then read `undefined`, which is what they mean.
  const parameters: AppParameters = app?.parameters ?? {}
  let requestTimeOut = false
  let isUsageEmpty = true
  let appUsageAndOverages: AppUsageAndOverages = zeroUsage(parameters)

  try {
    const billingTypeCurrent = checkBillingType(app)

    // A live trial bills from creation to trial end, not from the plan term.
    if (app.plan === 'trial' && getTrialLeftInDays(app.trialEndsAt) > 0) {
      app.currentTermStart = app.createdAt
      app.currentTermEnd = app.trialEndsAt
    }
    if (payload.fromDate !== undefined && payload.toDate !== undefined) {
      app.currentTermStart = payload.fromDate
      app.currentTermEnd = payload.toDate
    }

    // Apps with no billing term never reach the API, so they always read zero.
    if (app.currentTermStart !== undefined && app.currentTermEnd !== undefined) {
      const success: BillingCycleUsage | MetricsServiceError = yield call(
        getBillingCycleUsageMetrics,
        app,
      )
      const usage = success as BillingCycleUsage

      // The response is flat, so presence of `users` is the success test.
      if (usage.users !== undefined) {
        // Divide by the billing type's own allowance; the frozen apps saga still
        // uses an array literal here, which yields NaN. See PROGRESS.md.
        let overageParamForBillingType = parameters['overages.mau.included'] as number
        if (billingTypeCurrent === 'dcu') {
          overageParamForBillingType = parameters['overages.dcu.included'] as number
        } else if (billingTypeCurrent === 'mcu') {
          overageParamForBillingType = parameters['overages.mcu.included'] as number
        }

        isUsageEmpty = false
        appUsageAndOverages = {
          currentUsageUsers: usage.users,
          ccu: usage.ccu,
          voice_minutes: usage.voice_minutes,
          video_minutes: usage.video_minutes,
          mauLimit: parameters['overages.mau.included'] as number,
          ccuLimit: parameters['overages.ccu.included'] as number,
          mcuLimit: parameters['overages.mcu.included'] as number,
          dcuLimit: parameters['overages.dcu.included'] as number,
          voice_minutes_limit: parameters['overages.calling.included'] as number,
          video_minutes_limit: parameters['overages.calling.included'] as number,
          currentUsageUsersUsed: (usage.users / overageParamForBillingType) * 100,
          ccuUsed: (usage.ccu / (parameters['overages.ccu.included'] as number)) * 100,
          voiceMinutesUsed:
            (usage.voice_minutes / (parameters['overages.calling.included'] as number)) * 100,
          videoMinutesUsed:
            (usage.video_minutes / (parameters['overages.calling.included'] as number)) * 100,
          // ⚠️ PORTED BUG — the API field is `recording_minutes`; live reads
          // `recorded_minutes`, so this is always undefined. Kept as-is.
          recorded_minutes: ((usage as unknown) as { recorded_minutes: number }).recorded_minutes,
          mau_overage: 0,
          ccu_overage: 0,
          fromDate: getTimestampAsFullHumanDateOrdinal(app.currentTermStart),
          toDate: getTimestampAsFullHumanDateOrdinal(app.currentTermEnd),
        }

        // Overages only count when the plan enables them AND usage exceeds the
        // included allowance.
        if (
          payload.overagesEmpty === undefined &&
          parameters !== undefined &&
          parameters['overages.ccu.enabled'] !== undefined &&
          parameters['overages.ccu.enabled'] &&
          parameters['overages.ccu.included'] !== undefined &&
          usage.ccu > (parameters['overages.ccu.included'] as number)
        ) {
          appUsageAndOverages.ccu_overage =
            usage.ccu - (parameters['overages.ccu.included'] as number)
        }
        if (
          payload.overagesEmpty === undefined &&
          parameters !== undefined &&
          parameters['overages.mau.enabled'] !== undefined &&
          parameters['overages.mau.enabled'] &&
          parameters['overages.mau.included'] !== undefined &&
          usage.users > (parameters['overages.mau.included'] as number)
        ) {
          appUsageAndOverages.mau_overage =
            usage.users - (parameters['overages.mau.included'] as number)
        }
      } else if ((success as MetricsServiceError).err_code !== undefined) {
        requestTimeOut = true
      }
    }

    if (isUsageEmpty) {
      appUsageAndOverages = zeroUsage(parameters)
    }

    if (requestTimeOut) {
      window.triggerNotification?.('Request Time-out ', 'error')
      appUsageAndOverages = zeroUsage(parameters)
    }

    // Only redirect when asked — redirecting unconditionally strips query params
    // such as the builder's `?flow=cli`.
    if (redirectToIntegrate) {
      if (
        !(
          appUsageAndOverages.currentUsageUsers ||
          appUsageAndOverages.voice_minutes ||
          appUsageAndOverages.ccu ||
          appUsageAndOverages.video_minutes
        ) &&
        !_.includes(constants.trialPlansIDList, app.plan)
      ) {
        yield put(push(`/app/${app.id}/messaging-get-started`))
      } else if (callSourceRedirectURL) {
        yield put(push(callSourceRedirectURL))
      }
    }

    yield put({
      type: actions.SET_STATE,
      payload: {
        appUsageAndOverages,
        appUsageAndOveragesLoading: false,
      },
    })
  } catch (error) {
    // Emit the zero shape, not just the loading flag — the initial `[]` is not
    // nullish, so a `?? {}` fallback never fires and meters render blank.
    yield put({
      type: actions.SET_STATE,
      payload: {
        appUsageAndOverages: zeroUsage(parameters),
        appUsageAndOveragesLoading: false,
      },
    })
    window.triggerNotification?.('Failed to fetch usage data', 'error')
  }

  return appUsageAndOverages
}

export function* GET_INSIGHTS_DATA({
  payload = {},
}: {
  payload?: { activeApp?: unknown }
}): Generator<unknown, void, any> {
  try {
    const fetchedInsightsData = yield call(getInsightsData, payload.activeApp as string)

    // Live guards on the shape rather than truthiness: the service resolves
    // `false` when there is no bearer token, and can resolve `null`.
    if (typeof fetchedInsightsData !== 'boolean' && fetchedInsightsData !== null) {
      yield put({
        type: actions.SET_STATE,
        payload: { fetchedInsightsData },
      })
    }
  } catch (error) {
    window.triggerNotification?.('Failed to fetch insights data', 'error')
  }
}

/**
 * "Generate Report" — EMAILS a usage report to the app owner.
 * Writes no usage state on purpose.
 */
export function* MULTITENANT_GET_USAGE({
  payload,
}: {
  payload?: unknown
}): Generator<unknown, MultiTenantUsageResponse | boolean, any> {
  const activeApp = getRoot().apps.active

  const success: MultiTenantUsageResponse | boolean = yield call(
    requestMultiTenantBillingUsage,
    payload,
    activeApp,
  )
  const response = success as MultiTenantUsageResponse

  if (response && response.data) {
    window.triggerNotification?.(
      'The report has been successfully shared with the app owner via email.',
      'success',
    )
  } else {
    let errorMessage = 'Some error occurred, please try again'
    if (response.error?.code !== undefined && response.error?.message !== undefined) {
      errorMessage = response.error.message
    }
    window.triggerNotification?.(errorMessage, 'error')
  }

  return success
}

export default function* billingSaga() {
  yield all([
    takeEvery(actions.GET_USAGE_AND_OVERAGES, GET_USAGE_AND_OVERAGES as SagaWorker),
    takeEvery(actions.GET_INSIGHTS_DATA, GET_INSIGHTS_DATA as SagaWorker),
    takeEvery(actions.MULTITENANT_GET_USAGE, MULTITENANT_GET_USAGE as SagaWorker),
  ])
}
